/**
 * Content Block Library
 *
 * Defines optional content blocks that can be added to contract sections.
 * Organized by section type and category for easy discovery.
 */

import type { ContentLibrary } from './contract-types';
import { generateId } from './contract-utils';

/**
 * Content Library - Optional blocks that can be added to sections
 *
 * Structure:
 * - Each section type can have optional blocks
 * - Blocks are grouped by category for UI organization
 * - Each block contains default content that gets cloned when added
 */
export const CONTENT_LIBRARY: ContentLibrary = {
  // ============================================================================
  // COMPENSATION STRUCTURE - Optional compensation types
  // ============================================================================
  compensation_structure: {
    sectionType: 'compensation_structure',
    optionalBlocks: [
      {
        id: 'mentorship_program',
        label: 'Mentorship Program',
        description: 'Add mentorship program for first 5 brokered deals with 50/50 split',
        category: 'Special Programs',
        defaultContent: [
          {
            id: generateId('content'),
            type: 'heading',
            text: 'D. Mentorship Program',
            level: 3
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Lendwise will provide mentorship support through the Company\'s mentorship program. {{mentorName}} will serve as your assigned mentor for your first five (5) brokered loan transactions.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Phase 1: Training Period (First 5 Brokered Deals)',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'During the mentorship period, your broker commission split ({{brokerCommissionSplit:percentage}}) will be allocated between you and your assigned mentor as follows:'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'You receive: {{loanOfficerMentorSplit:percentage}} of your allocated broker commission split',
              '{{mentorName}} receives: {{brokerMentorCommissionSplit:percentage}} of your allocated broker commission split'
            ]
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'Example: If the Company earns a $10,000 broker commission and your split is {{brokerCommissionSplit:percentage}} ({{mentorshipLoAllocation:currency}}), you receive {{mentorshipLoReceives:currency}} and {{mentorName}} receives {{mentorshipMentorReceives:currency}}. The Company retains {{mentorshipCompanyRetains:currency}}.'
          },
          {
            id: generateId('content'),
            type: 'heading',
            text: 'Phase 2: Standard Compensation (After 5 Brokered Deals)',
            level: 4
          },
          {
            id: generateId('content'),
            type: 'paragraph',
            text: 'After completing 5 brokered deals, you automatically transition to standard compensation.'
          },
          {
            id: generateId('content'),
            type: 'bulleted_list',
            items: [
              'You will receive 100% of the agreed-upon brokered commission split.'
            ]
          }
        ]
      }
    ]
  },

  // ============================================================================
  // PAYMENT TERMS - Optional payment structures
  // ============================================================================
  payment_terms: null,

  // ============================================================================
  // Other sections - Add optional blocks as needed
  // ============================================================================
  loan_officer_info: null,
  employment_classification: null,
  recapture_clawback: null,
  compliance_licensing: null,
  at_will_employment: null,
  performance_expectations: null,
  expenses_fees: null,
  outside_sales: null,
  confidentiality: null,
  dispute_resolution: null,
  pipeline_ownership: null,
  independent_contractor: null,
  legal_provisions: null,
  agreement_summary: null,
  acknowledgment_checklist: null,
  signature_block: null
};
